// src/utils/performanceOptimizer.js
/**
 * Performance monitoring and optimization utilities for the subscription management system
 */

class PerformanceOptimizer {
  constructor() {
    this.metrics = {
      apiCalls: new Map(),
      componentRenders: new Map(),
      cacheHits: 0,
      cacheMisses: 0,
      tokenRefreshes: 0
    };
    this.startTime = performance.now();
  }

  /**
   * Track API call performance
   */
  trackApiCall(endpoint, duration, cached = false) {
    if (!this.metrics.apiCalls.has(endpoint)) {
      this.metrics.apiCalls.set(endpoint, {
        count: 0,
        totalDuration: 0,
        averageDuration: 0,
        cacheHits: 0,
        slowCalls: 0 // calls > 1000ms
      });
    }

    const metric = this.metrics.apiCalls.get(endpoint);
    metric.count++;
    
    if (cached) {
      metric.cacheHits++;
      this.metrics.cacheHits++;
    } else {
      this.metrics.cacheMisses++;
      metric.totalDuration += duration;
      
      if (duration > 1000) {
        metric.slowCalls++;
      }
    }
    
    metric.averageDuration = metric.totalDuration / (metric.count - metric.cacheHits);
  }

  /**
   * Track component render performance
   */
  trackComponentRender(componentName, duration) {
    if (!this.metrics.componentRenders.has(componentName)) {
      this.metrics.componentRenders.set(componentName, {
        count: 0,
        totalDuration: 0,
        averageDuration: 0,
        slowRenders: 0 // renders > 100ms
      });
    }

    const metric = this.metrics.componentRenders.get(componentName);
    metric.count++;
    metric.totalDuration += duration;
    metric.averageDuration = metric.totalDuration / metric.count;
    
    if (duration > 100) {
      metric.slowRenders++;
    }
  }

  /**
   * Track token refresh events
   */
  trackTokenRefresh() {
    this.metrics.tokenRefreshes++;
  }

  /**
   * Get performance report
   */
  getPerformanceReport() {
    const now = performance.now();
    const totalTime = now - this.startTime;

    const report = {
      sessionDuration: Math.round(totalTime),
      cacheEfficiency: {
        hits: this.metrics.cacheHits,
        misses: this.metrics.cacheMisses,
        hitRate: this.metrics.cacheHits + this.metrics.cacheMisses > 0 
          ? Math.round((this.metrics.cacheHits / (this.metrics.cacheHits + this.metrics.cacheMisses)) * 100)
          : 0
      },
      tokenRefreshes: this.metrics.tokenRefreshes,
      apiCalls: {},
      componentRenders: {},
      recommendations: []
    };

    // Process API calls
    for (const [endpoint, metric] of this.metrics.apiCalls) {
      report.apiCalls[endpoint] = {
        ...metric,
        cacheHitRate: metric.count > 0 ? Math.round((metric.cacheHits / metric.count) * 100) : 0
      };
    }

    // Process component renders
    for (const [component, metric] of this.metrics.componentRenders) {
      report.componentRenders[component] = metric;
    }

    // Generate recommendations
    report.recommendations = this.generateRecommendations();

    return report;
  }

  /**
   * Generate performance recommendations
   */
  generateRecommendations() {
    const recommendations = [];

    // Cache efficiency recommendations
    const totalRequests = this.metrics.cacheHits + this.metrics.cacheMisses;
    if (totalRequests > 10) {
      const hitRate = (this.metrics.cacheHits / totalRequests) * 100;
      
      if (hitRate < 30) {
        recommendations.push({
          type: 'cache',
          severity: 'high',
          message: `Low cache hit rate (${hitRate.toFixed(1)}%). Consider increasing cache TTL for stable endpoints.`
        });
      } else if (hitRate > 80) {
        recommendations.push({
          type: 'cache',
          severity: 'info',
          message: `Excellent cache hit rate (${hitRate.toFixed(1)}%)! Cache optimization is working well.`
        });
      }
    }

    // API performance recommendations
    for (const [endpoint, metric] of this.metrics.apiCalls) {
      if (metric.averageDuration > 2000) {
        recommendations.push({
          type: 'api',
          severity: 'high',
          message: `Slow API endpoint: ${endpoint} (${metric.averageDuration.toFixed(0)}ms average). Consider optimization.`
        });
      }
      
      if (metric.slowCalls > metric.count * 0.3) {
        recommendations.push({
          type: 'api',
          severity: 'medium',
          message: `${endpoint} has ${metric.slowCalls} slow calls out of ${metric.count}. Consider backend optimization.`
        });
      }
    }

    // Component render recommendations
    for (const [component, metric] of this.metrics.componentRenders) {
      if (metric.averageDuration > 200) {
        recommendations.push({
          type: 'component',
          severity: 'high',
          message: `Slow component: ${component} (${metric.averageDuration.toFixed(0)}ms average). Consider React.memo or optimization.`
        });
      }
      
      if (metric.count > 20 && metric.slowRenders > metric.count * 0.2) {
        recommendations.push({
          type: 'component',
          severity: 'medium',
          message: `${component} has frequent slow renders (${metric.slowRenders}/${metric.count}). Consider optimization.`
        });
      }
    }

    // Token refresh recommendations
    if (this.metrics.tokenRefreshes > 5) {
      recommendations.push({
        type: 'auth',
        severity: 'medium',
        message: `High token refresh count (${this.metrics.tokenRefreshes}). Consider increasing token TTL or checking for unnecessary refreshes.`
      });
    }

    return recommendations;
  }

  /**
   * Export metrics for external logging
   */
  exportMetrics() {
    return {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      metrics: this.getPerformanceReport()
    };
  }

  /**
   * Reset metrics
   */
  reset() {
    this.metrics = {
      apiCalls: new Map(),
      componentRenders: new Map(),
      cacheHits: 0,
      cacheMisses: 0,
      tokenRefreshes: 0
    };
    this.startTime = performance.now();
  }

  /**
   * Log performance report to console (development only)
   */
  logReport() {
    if (process.env.NODE_ENV !== 'development') {
      return;
    }

    const report = this.getPerformanceReport();
    
    console.group('📊 Performance Report');
    console.log('Session Duration:', `${(report.sessionDuration / 1000).toFixed(1)}s`);
    console.log('Cache Hit Rate:', `${report.cacheEfficiency.hitRate}%`);
    console.log('Token Refreshes:', report.tokenRefreshes);
    
    if (Object.keys(report.apiCalls).length > 0) {
      console.group('🌐 API Performance');
      for (const [endpoint, metric] of Object.entries(report.apiCalls)) {
        console.log(`${endpoint}:`, {
          calls: metric.count,
          avgDuration: `${metric.averageDuration.toFixed(0)}ms`,
          cacheHitRate: `${metric.cacheHitRate}%`,
          slowCalls: metric.slowCalls
        });
      }
      console.groupEnd();
    }
    
    if (Object.keys(report.componentRenders).length > 0) {
      console.group('⚛️ Component Performance');
      for (const [component, metric] of Object.entries(report.componentRenders)) {
        console.log(`${component}:`, {
          renders: metric.count,
          avgDuration: `${metric.averageDuration.toFixed(1)}ms`,
          slowRenders: metric.slowRenders
        });
      }
      console.groupEnd();
    }
    
    if (report.recommendations.length > 0) {
      console.group('💡 Recommendations');
      report.recommendations.forEach(rec => {
        const icon = rec.severity === 'high' ? '🚨' : rec.severity === 'medium' ? '⚠️' : 'ℹ️';
        console.log(`${icon} ${rec.message}`);
      });
      console.groupEnd();
    }
    
    console.groupEnd();
  }
}

// Create singleton instance
const performanceOptimizer = new PerformanceOptimizer();

// Log performance report every 5 minutes in development
if (process.env.NODE_ENV === 'development') {
  setInterval(() => {
    performanceOptimizer.logReport();
  }, 5 * 60 * 1000);
}

// Export metrics before page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (process.env.NODE_ENV === 'development') {
      performanceOptimizer.logReport();
    }
  });
}

export default performanceOptimizer;